import React, { useContext, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { useEntidad, useForm, useLista, } from '../../components/hooks';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ServerRequest } from '../../utils/apiweb';
import {
    Loading,
    TableCustom,
    SectionHeading,
    InputEntidad,
    InputPersona,
    AdvancedSearch,
    InputWithEnterEvent,
    FooterButtonVolver
} from '../../components/common';
import ControladorModal from '../../components/controls/ControladorModal';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { ManagedContext } from '../../context/custom';
import { PERMISSIONS } from '../../consts/permissions';
import { usePermissions } from '../../components/hooks/usePermissions';

function ControladoresView() {

    //hooks
    const { showMessageModal } = useContext(ManagedContext)
    const location = useLocation()
    const previousFilters = location.state?.filters

    const [state, setState] = useState({
        loading: false,
        showForm: false,
        modeFormEdit: false,
        rowId: 0,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const [tableVisible, setTableVisible] = useState(false);

    const { hasPermission } = usePermissions();
    const [searchParams] = useSearchParams();
    const canEdit = hasPermission(PERMISSIONS.CONTROLADOR_EDIT);
    const isViewOnly = searchParams.get('mode') === OPERATION_MODE.VIEW;
    const allowEditActions = canEdit && !isViewOnly;

    const [filters, setFilters] = useState({
        labels: [
            { title: 'Tipo de controlador', field: 'descTipoControlador', value: '', valueIgnore: '', fieldOriginal: 'idTipoControlador', valueIgnoreOriginal: 0},
            { title: 'Persona', field: 'descPersona', value: '', valueIgnore: '', fieldOriginal: 'idPersona', valueIgnoreOriginal: 0},
            { title: 'Supervisor', field: 'descControladorSupervisor', value: '', valueIgnore: '', fieldOriginal: 'idControladorSupervisor', valueIgnoreOriginal: 0 },
            { title: 'Etiqueta', field: 'etiqueta', value: '', valueIgnore: '' }
        ]
    });

    const [ formValues, formHandle, , formSet ] = useForm({
        idTipoControlador: 0,
        descTipoControlador: '', //no existe como control, se usa para tomar el text del tipo
        idPersona: 0,
        descPersona: '', //no existe como control, se usa para tomar el text de idPersona
        idControladorSupervisor: 0,
        descControladorSupervisor: '', //no existe como control, se usa para tomar el text de supervisor
        etiqueta: ''
    });

    const [, getRowLista ] = useLista({
        listas: ['TipoDocumento'],
        onLoaded: (listas, isSuccess, error) => {
          if (!isSuccess) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoDocumento',
          timeout: 0
        }
    });

    const [, getRowEntidad] = useEntidad({
        entidades: ['TipoControlador'],
        onLoaded: (entidades, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoControlador',
          timeout: 0
        }
    });

    const getDescTipoControlador = (id) => {
        const row = getRowEntidad('TipoControlador', id);
        return (row) ? row.nombre : '';
    }

    const getDescTipoDocumento = (id) => {
        const row = getRowLista('TipoDocumento', id);
        return (row) ? row.nombre : '';
    }

    //definiciones
    const tableColumns = [
        { Header: 'Tipo de controlador', Cell: (props) => `${getDescTipoControlador(props.value)}${props.row.original.abogadoPorDefecto ? ' (por defecto)' : ''}`, accessor: 'idTipoControlador', width: '25%' },
        { Header: 'Documento', Cell: (props) => {
            const row = props.row.original;
            const tipoDocumento = getDescTipoDocumento(row.idTipoDocumento);
            return `${tipoDocumento} ${row.numeroDocumento}`;
        }, id: 'documento', accessor: 'documento', width: '25%' },
        { Header: 'Nombre y apellido', accessor: 'nombrePersona', width: '40%' },
    ];

    //handles
    const avmr = {
        onAdd: () => {
            setState(prevState => ({...prevState, showForm: true, modeFormEdit: true, rowId: 0}))
        },
        onView: ({ id }) => {
            setState(prevState => ({...prevState, showForm: true, modeFormEdit: false, rowId: parseInt(id)}))
        },
        onModify: ({ id }) => {
            setState(prevState => ({...prevState, showForm: true, modeFormEdit: true, rowId: parseInt(id)}))   
        },
        onRemove: ({ id }) => {
            const controlador = state.list.find(x => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el controlador ${controlador.nombrePersona}?`,
                onConfirm: () => RemoveControlador(id),
            })
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setState(prevState => ({...prevState, dataTagger: { showModal: true, idEntidad: id, disabled }}))
        },
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }

    //funciones
    function SearchControladores() {

        setState(prevState => {
            return {...prevState, loading: true, list: []};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {
                data.forEach(x => {
                    if (x.fechaCreacion) x.fechaCreacion = new Date(x.fechaCreacion);
                });                
                setState(prevState => {
                    return {...prevState, loading: false, list: data};
                });
                setTableVisible(true);
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/filter?`+
        `idTipoControlador=${formValues.idTipoControlador}&`+
        `idPersona=${formValues.idPersona}&`+
        `idControladorSupervisor=${formValues.idControladorSupervisor}&`+
        `etiqueta=${encodeURIComponent(formValues.etiqueta)}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CONTROLADOR,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function RemoveControlador(id) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchControladores();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.CONTROLADOR,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }


    function ApplyFilters() {
        UpdateFilters();
        SearchControladores();
    }
    function UpdateFilters() {
        let labels = [...filters.labels];
        labels.forEach((label) => {
            label.value = formValues[label.field];
        });
        setFilters(prevState => {
            return {...prevState, labels: labels};
        });
    }

    const dataTaggerRegistro = state.list.find(x => x.id === state.dataTagger.idEntidad)?.nombrePersona ?? ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de controlador"
                registro={dataTaggerRegistro}
                entidad="Controlador"
                idEntidad={state.dataTagger.idEntidad}
                disabled={state.dataTagger.disabled}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        {state.showForm && 
            <ControladorModal
                id={state.rowId}
                disabled={!state.modeFormEdit}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, showForm: false};
                    });
                }}
                onConfirm={(id) => {
                    setState(prevState => {
                        return {...prevState, showForm: false, rowId: 0};
                    });
                    if (tableVisible) SearchControladores();
                }}
            />
        }

        <SectionHeading titles={[{ title: 'Entidades' }, { title: 'Administración de listas', url: '/entidades' }, { title: 'Controladores' }]}
                        onAddClick={allowEditActions ? avmr.onAdd : null} {...{tableVisible}} />

        <section className='section-accordion'>

            <AdvancedSearch
                formValues={formValues}
                formSet={formSet}
                labels={filters.labels.filter(f => f.value !== f.valueIgnore)}
                onSearch={ApplyFilters}
            >

                <div className='row form-basic'>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="idTipoControlador" className="form-label">Tipo de controlador</label>
                        <InputEntidad
                            name="idTipoControlador"
                            placeholder=""
                            className="form-control"
                            value={ formValues.idTipoControlador }
                            onChange={({target}) =>{
                                let idTipoControlador = 0;
                                let descTipoControlador = '';
                                if (target.row) {
                                    idTipoControlador = parseInt(target.value);
                                    descTipoControlador = target.row.nombre;
                                }
                                formSet({...formValues, idTipoControlador: idTipoControlador, descTipoControlador: descTipoControlador});
                            }}
                            title="Tipo controlador"
                            entidad="TipoControlador"
                            onFormat= {(row) => (row) ? `${row.codigo} - ${row.nombre}` : ''}
                        />                        
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="idControladorSupervisor" className="form-label">Supervisor/a</label>
                        <InputEntidad
                            name="idControladorSupervisor"
                            placeholder=""
                            className="form-control"
                            value={ formValues.idControladorSupervisor }
                            onChange={({target}) =>{
                                let idControladorSupervisor = 0;
                                let descControladorSupervisor = '';
                                if (target.row) {
                                    idControladorSupervisor = parseInt(target.value);
                                    descControladorSupervisor = target.row.nombrePersona;
                                }
                                formSet({...formValues, idControladorSupervisor: idControladorSupervisor, descControladorSupervisor: descControladorSupervisor});
                            }}
                            title="Supervisor"
                            entidad="Controlador"
                            onFormat={(row) => (row && row.id) ? `${row.nombrePersona} (Legajo: ${row.legajo})` : ''}
                            filter={(row) => { return row.esSupervisor; }}
                            columns={[
                                { Header: 'Legajo', accessor: 'legajo', width: '25%' },
                                { Header: 'Nombre y apellido', accessor: 'nombrePersona', width: '70%' }
                            ]}
                            memo={false}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="idPersona" className="form-label">Persona</label>
                        <InputPersona
                            name="idPersona"
                            placeholder=""
                            className="form-control"
                            value={ formValues.idPersona }
                            idTipoPersona={0}
                            onChange={({target}) =>{
                                let idPersona = 0;
                                let descPersona = '';
                                if (target.row) {
                                    idPersona = parseInt(target.value);
                                    descPersona = target.row.nombrePersona;
                                }
                                formSet({...formValues, idPersona: idPersona, descPersona: descPersona});
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-2">
                        <label htmlFor="etiqueta" className="form-label">Etiqueta</label>
                        <InputWithEnterEvent
                            name="etiqueta"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={ formValues.etiqueta }
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                </div>

            </AdvancedSearch>

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    avmr={avmr}
                    editCode={PERMISSIONS.CONTROLADOR_EDIT}
                    disabled={!allowEditActions}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                <FooterButtonVolver filters={previousFilters} to="/entidades" />
            </div>
        </footer>
        
    </>
    )
}

export default ControladoresView;
